import { gql } from '@apollo/client';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { DownloadIcon, MoreIconVertical } from '~components/ui/icons';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { PageLoader } from '~components/ui/PageLoader';
import { PageSizeSelector } from '~components/ui/PageSizeSelector';
import { PaginationServerSide } from '~components/ui/PaginationServerSide';
import { ServiceAdminDeviceLogStatus } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { DeviceLogsQuery, useDeviceLogsQuery } from './__generated__/DeviceLogsModal.graphql';
import { TruncatedCell } from './AccessRequestsSection';

const PAGE_SIZES = [8, 25, 50, 100];

type DeviceLog = DeviceLogsQuery['serviceAdminDeviceLogs']['results'][number];

const statusLabels: Record<ServiceAdminDeviceLogStatus, string> = {
  [ServiceAdminDeviceLogStatus.Starting]: 'deviceLogStatusStarting',
  [ServiceAdminDeviceLogStatus.Ongoing]: 'deviceLogStatusOngoing',
  [ServiceAdminDeviceLogStatus.Stopping]: 'deviceLogStatusStopping',
  [ServiceAdminDeviceLogStatus.Stopped]: 'deviceLogStatusStopped',
  [ServiceAdminDeviceLogStatus.Uploading]: 'deviceLogStatusUploading',
  [ServiceAdminDeviceLogStatus.Completed]: 'deviceLogStatusCompleted',
  [ServiceAdminDeviceLogStatus.Error]: 'deviceLogStatusError',
};

interface DeviceLogRow {
  targetOrg: string;
  customer: string;
  deviceId: string;
  startTime: string;
  endTime: string;
  startedBy: string;
  status: string;
}

interface DeviceLogColumn {
  id: keyof DeviceLogRow;
  header: string;
  maxWidth?: string;
}

function getColumns(): DeviceLogColumn[] {
  return [
    { id: 'targetOrg', header: t('deviceLogTargetOrg'), maxWidth: '40' },
    { id: 'customer', header: t('customer'), maxWidth: '40' },
    { id: 'deviceId', header: t('deviceId'), maxWidth: '40' },
    { id: 'startTime', header: t('startTime') },
    { id: 'endTime', header: t('deviceLogEndTime') },
    { id: 'startedBy', header: t('deviceLogStartedBy'), maxWidth: '48' },
    { id: 'status', header: t('status') },
  ];
}

function formatTime(value: string | null | undefined) {
  return value ? format(new Date(value), 'MMM d, y, HH:mm') : '-';
}

function toDeviceLogRow(log: DeviceLog): DeviceLogRow {
  return {
    targetOrg: log.organizationName,
    customer: log.customerName,
    deviceId: log.deviceId,
    startTime: formatTime(log.startTime),
    endTime: formatTime(log.endTime),
    startedBy: log.startedByEmail,
    status: t(statusLabels[log.status]),
  };
}

interface Props {
  accessRequestId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function DeviceLogsModal({ accessRequestId, isOpen, onClose }: Props) {
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  const { data, previousData, loading, error } = useDeviceLogsQuery({
    variables: { accessRequestId, pageSize, pageNumber: pageIndex },
    fetchPolicy: 'cache-and-network',
    skip: !isOpen,
  });

  const page = (data ?? previousData)?.serviceAdminDeviceLogs;
  const logs = page?.results ?? [];
  const pageCount = Math.ceil((page?.totalCount ?? 0) / pageSize);
  const columns = getColumns();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" autoFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader
          marginX="6"
          paddingX="0"
          borderBottom="1px solid"
          borderColor="gray.100"
          fontSize="xl"
        >
          {t('deviceLogsTitle')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody paddingTop="4">
          {error ? (
            <HandleApiError error={error} />
          ) : (
            <>
              <Box
                boxShadow="elevated"
                border="1px solid"
                borderRadius="md"
                borderColor="gray.100"
                overflowX="auto"
                overflowY="auto"
                maxHeight="60vh"
                bgColor="white"
              >
                {loading && !page ? (
                  <Box padding="8">
                    <PageLoader />
                  </Box>
                ) : (
                  <Table variant="simple" width="100%">
                    <Thead bgColor="gray.50" position="sticky" top="0" zIndex="1">
                      <Tr>
                        {columns.map((column) => (
                          <Th
                            key={column.id}
                            color="gray.700"
                            textTransform="none"
                            whiteSpace="nowrap"
                            letterSpacing="normal"
                          >
                            {column.header}
                          </Th>
                        ))}
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {logs.length === 0 ? (
                        <Tr>
                          <Td colSpan={columns.length + 1} textAlign="center" color="gray.500">
                            {t('noDeviceLogs')}
                          </Td>
                        </Tr>
                      ) : (
                        logs.map((log) => {
                          const row = toDeviceLogRow(log);

                          return (
                            <Tr key={log.id}>
                              {columns.map((column) => (
                                <Td key={column.id} whiteSpace="nowrap">
                                  {column.maxWidth ? (
                                    <TruncatedCell
                                      text={row[column.id]}
                                      maxWidth={column.maxWidth}
                                    />
                                  ) : (
                                    row[column.id]
                                  )}
                                </Td>
                              ))}
                              <Td width="16">
                                <DeviceLogActions logUrl={log.logUrl} />
                              </Td>
                            </Tr>
                          );
                        })
                      )}
                    </Tbody>
                  </Table>
                )}
              </Box>

              <Box display="flex" marginTop="4">
                <Box flex="1">
                  <PageSizeSelector
                    label={t('showRowsPerPage')}
                    options={PAGE_SIZES}
                    value={pageSize}
                    onChange={setPageSize}
                  />
                </Box>
                <PaginationServerSide
                  pageCount={pageCount}
                  currentPageIndex={pageIndex}
                  hasPreviousPage={pageIndex > 0}
                  hasNextPage={pageIndex < pageCount - 1}
                  goToPage={setPageIndex}
                  goToPreviousPage={() => setPageIndex(pageIndex - 1)}
                  goToNextPage={() => setPageIndex(pageIndex + 1)}
                />
              </Box>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" colorScheme="gray" onClick={onClose}>
            {t('cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function DeviceLogActions({ logUrl }: { logUrl?: string | null }) {
  const toast = useToast();

  const handleDownload = () => {
    if (!logUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = logUrl;
    link.setAttribute('download', '');
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({ status: 'success', title: t('logsDownloadedSuccessfully') });
  };

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={IconButton}
        variant="unstyled"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minWidth="8"
        height="8"
        aria-label={t('actions')}
        icon={<MoreIconVertical color="blue.400" width="5" height="5" />}
      />
      <MenuList>
        <MenuItem icon={<DownloadIcon />} isDisabled={!logUrl} onClick={handleDownload}>
          {t('downloadLogs')}
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

DeviceLogsModal.graphql = {
  queries: {
    DeviceLogs: gql`
      query DeviceLogs($accessRequestId: ID!, $pageSize: Int, $pageNumber: Int) {
        serviceAdminDeviceLogs(
          accessRequestId: $accessRequestId
          pageSize: $pageSize
          pageNumber: $pageNumber
        ) {
          totalCount
          results {
            id
            organizationName
            customerName
            deviceId
            startTime
            endTime
            startedByEmail
            status
            logUrl
          }
        }
      }
    `,
  },
};
